/* eslint-disable @angular-eslint/prefer-inject */
import { ExceptionDataService } from '../infrastructure/exceptions/exception.data.service';
import {
    ExceptionCreate,
    ExceptionDto,
    ExceptionRead,
    ExceptionSearchParams,
} from '../entities/exception.dto';
import { ExceptionStore } from '../infrastructure/exceptions/exception.store';
import { inject, Injectable } from '@angular/core';
import { RoutesForException } from './exception.routes';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { ExceptionMapper } from '../infrastructure/exceptions/exception.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { IDocumentDto, IQueryParams, SafeAny } from '@pf/shared-common';
import { ExceptionDocumentsFacade } from './exception-documents.facade';
import { combineLatest, map, Observable } from 'rxjs';
import {
    ExceptionStatus,
    ResourceAuth,
    WmsPlatformEntity,
} from '@control-tower/platform-wms';
import { orderBy } from 'lodash-es';
import { WarehouseFacade } from './warehouse.facade';
import { subBusinessDays } from 'date-fns';
import { StatusOptions } from '@pf/shared-ui';

export const InProgressExceptionStatuses: ExceptionStatus[] = [
    ExceptionStatus.Open,
    ExceptionStatus.InProgress,
    ExceptionStatus.OnHold,
    ExceptionStatus.Escalated,
];

@Injectable()
export class ExceptionFacade extends AbstractManageEntityFacade<
    ExceptionDto,
    ExceptionRead,
    ExceptionCreate,
    ExceptionSearchParams
> {
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly documentFacade = inject(ExceptionDocumentsFacade);
    nameField: keyof ExceptionDto = 'exceptionType';

    constructor(
        dataService: ExceptionDataService,
        routes: RoutesForException,
        store: ExceptionStore,
        mapper: ExceptionMapper
    ) {
        super(dataService, routes, store, mapper);
    }

    override getEntityName(exception: ExceptionDto) {
        return `${exception.sourceEntity} (${exception.exceptionType?.name})`;
    }

    exceptionStatusOptions: StatusOptions = {
        [ExceptionStatus.Open]: {
            label: 'Open',
            status: 'primary',
        },
        [ExceptionStatus.Closed]: {
            label: 'Closed',
            color: '#808080',
        },
        [ExceptionStatus.OnHold]: {
            label: 'OnHold',
            status: 'danger',
        },
        [ExceptionStatus.Escalated]: {
            label: 'Escalated',
            color: '#f65c43',
        },
        [ExceptionStatus.InProgress]: {
            label: 'In Progress',
            status: 'warning',
        },
        [ExceptionStatus.Resolved]: {
            label: 'Resolved',
            status: 'success',
        },
        defaultOption: {
            status: 'secondary',
            label: 'Unknown',
        },
    };

    inProgressExceptions$ = this.store.filter$((exception: ExceptionRead) => {
        return (
            !exception.isDeleted &&
            exception.status &&
            InProgressExceptionStatuses.includes(exception.status)
        );
    });

    override getDefaultSearchParams$(
        params: IQueryParams<ExceptionDto>
    ): Observable<IQueryParams<ExceptionDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: ExceptionCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ExceptionDto, string>> {
        return { exceptionType: searchText };
    }

    saveDocuments$(id: string, files: IDocumentDto[]) {
        return combineLatest(
            files.map(file => this.documentFacade.create$(id, file))
        );
    }

    getException$(entity: WmsPlatformEntity, entityId: string) {
        function filterFn(exception: ExceptionRead) {
            return (
                exception.sourceEntity === entity &&
                exception.sourceEntityId === entityId
            );
        }

        const existingExceptions = this.store.filter(filterFn);

        if (!existingExceptions.length) {
            this.getEntityExceptions$(entity, entityId).subscribe();
        }

        return this.store.filter$(filterFn).pipe(
            map(exceptions => {
                return exceptions.length > 0
                    ? orderBy(exceptions, ['createdAt'], ['desc'])[0]
                    : null;
            })
        );
    }

    filterBySourceEntityId$(receiptLineId: string): Observable<ExceptionDto[]> {
        return this.store
            .filter$((exception: ExceptionRead) => {
                return exception.sourceEntityId === receiptLineId;
            })
            .pipe(map(exceptions => this.mapper.toDTOList(exceptions)));
    }

    loadAllInProgressExceptions() {
        this.searchAll$(
            {
                pageIndex: 1,
                pageSize: 25,
                filters: {
                    status: InProgressExceptionStatuses,
                },
            },
            false
        ).subscribe();
    }

    loadRecentExceptions(sourceEntity: WmsPlatformEntity) {
        this.searchAll$({
            pageSize: 50,
            pageIndex: 1,
            filters: {
                sourceEntity,
                createdStartDate: subBusinessDays(new Date(), 3).toDateString(),
            } as SafeAny,
        }).subscribe();
    }

    getEntityExceptions$(
        sourceEntity: WmsPlatformEntity,
        sourceEntityId: string
    ) {
        return this.searchAll$({
            pageSize: 50,
            pageIndex: 1,
            filters: {
                sourceEntity,
                sourceEntityId,
            },
        });
    }
}
